import { SPHERES, SPHERE_IDS, THRESH, sphereLabel } from './spheres.js';
import { balance, lowSpheres } from './balance.js';
import { seedHash } from './observer.js';
import { DEFAULT_LOCALE } from './locale.js';
import { domainCopy } from './i18n/index.js';
import { BOOK_ANCHORS } from './book-anchors.js';
/**
 * наблюдатель 2.0: режиссёр по спеке Кати. сначала смотрит в матрицу — делает ли
 * человек хоть что-то, — потом в колесо и выбирает одну из 12 механик книги.
 * вывод ведёт в главы книги и в практику озера. без сети и без модели:
 * тот же вход и тот же seed всегда дают тот же текст.
 */
import { isDone } from './done.js';
import { goalStatus, isSecondMonth } from './goals.js';
export { isDone };
/** наблюдатель смотрит только на сегодняшний день, не на всю матрицу */
export function matrixDay(skills, today) {
    const per = {};
    const idle = [];
    let done = 0;
    for (const k of skills) {
        const on = isDone(k, today);
        if (on)
            done += 1;
        else
            idle.push(k.name);
        const acc = per[k.sphere] ?? [0, 0];
        per[k.sphere] = [acc[0] + (on ? 1 : 0), acc[1] + 1];
    }
    const bySphere = {};
    const zeroSpheres = [];
    let spheresTouched = 0;
    for (const id of SPHERE_IDS) {
        const acc = per[id];
        if (!acc)
            continue;
        bySphere[id] = acc[0] / acc[1];
        if (acc[0] === 0)
            zeroSpheres.push(id);
        else
            spheresTouched += 1;
    }
    return { skills: skills.length, done, rate: skills.length ? done / skills.length : 0, idle, bySphere, zeroSpheres, spheresTouched };
}
// ——— главы книги ———
const CHAPTER_IDS = {
    vychislit: 'glava-3-kak-seba-vycislit',
    panDionisAid: 'glava-5-pan-dionis-i-aid',
    nabludatel: 'glava-6-nabludatel',
    superpoz: 'glava-7-provokativnyj-podhod-i-superpozicia',
    pletenie: 'glava-8-pletenie-pautiny-sozdanie-konteksta',
    kapli: 'glava-9-klejkie-kapli-ustojcivost-sistemy',
    zaryad: 'glava-10-elektriceskij-zarad-set-tanetsa-k-emocii',
    vibracii: 'glava-11-vibracii-seti',
    estetika: 'glava-13-maskirovka-i-ukrasenia--estetika-seti',
    shelk: 'glava-14-ekonomika-selka-pererabotka-resursov',
    feromony: 'glava-15-feromony--igra-cerez-jelania',
    terpenie: 'glava-16-terpenie--sila-nepodvijnosti',
    evolucia: 'glava-17-evolucia-seti--stil-i-podpis',
};
const chapterRef = (key, t) => ({ chapterId: CHAPTER_IDS[key], hint: t.chapters[key] });
// ——— персоны: по целевой сфере механики ———
const PERSONA = {
    sostoyanie: 'dionis',
    zdorovie: 'pan',
    otnosheniya: 'bog-syn',
    prostranstvo: 'aid',
    dengi: 'svyatoy-duh',
    produkt: 'bog-otec',
    vliyanie: 'pan',
    realizatsiya: 'aid',
};
/** где в книге описан персонаж: ссылка ведет не в начало главы, а к нему */
const PERSONA_PLACE = {
    pan: { chapter: 'panDionisAid', anchorId: 'pan-vhod' },
    aid: { chapter: 'panDionisAid', anchorId: 'aid-pogruzhenie' },
    dionis: { chapter: 'panDionisAid', anchorId: 'dionis-ekstaz' },
    'bog-otec': { chapter: 'nabludatel', anchorId: 'shest-personazhey' },
    'bog-syn': { chapter: 'nabludatel', anchorId: 'shest-personazhey' },
    'svyatoy-duh': { chapter: 'nabludatel', anchorId: 'shest-personazhey' },
};
function wheelCtx(values, nm) {
    const low = lowSpheres(values);
    const sorted = [...SPHERE_IDS].sort((a, b) => values[a] - values[b]);
    const grp = (id) => SPHERES.find((s) => s.id === id)?.grp;
    return {
        values,
        nm,
        low,
        high: SPHERE_IDS.filter((id) => values[id] >= 8),
        weakest: sorted[0],
        top: sorted.at(-1),
        coh: balance(values),
        inLow: low.filter((id) => grp(id) === 'in').length,
        outLow: low.filter((id) => grp(id) === 'out').length,
    };
}
const pickOf = (arr, n) => arr[n % arr.length];
/** 12 механик из таблицы «наблюдатель 2.0»: глава книги → триггер → персона. тексты — в i18n */
export const MECHANICS = [
    {
        id: 'pravilo-dvuh',
        chapter: 'superpoz',
        test: (c) => c.low.length >= 2,
        target: (c) => c.weakest,
    },
    {
        id: 'deficit',
        chapter: 'pletenie',
        test: (c) => c.low.length >= 1,
        target: (c) => c.weakest,
    },
    {
        id: 'proficit',
        chapter: 'kapli',
        test: (c) => c.values[c.top] >= 9 && c.low.length >= 1,
        target: (c) => c.weakest,
    },
    {
        id: 'kapli',
        chapter: 'kapli',
        test: () => true,
        target: (c) => c.weakest,
    },
    {
        id: 'zaryad',
        chapter: 'zaryad',
        test: (c) => c.values.sostoyanie < THRESH || c.coh < 45,
        target: () => 'sostoyanie',
    },
    {
        id: 'vibracii',
        chapter: 'vibracii',
        test: (c) => c.values.otnosheniya < THRESH || c.values.vliyanie < THRESH,
        target: (c) => (c.values.otnosheniya <= c.values.vliyanie ? 'otnosheniya' : 'vliyanie'),
    },
    {
        id: 'feromony',
        chapter: 'feromony',
        test: (c) => c.values.vliyanie < THRESH,
        target: () => 'vliyanie',
    },
    {
        id: 'shelk',
        chapter: 'shelk',
        test: (c) => c.values.produkt < THRESH || c.values.realizatsiya < THRESH,
        target: (c) => (c.values.produkt <= c.values.realizatsiya ? 'produkt' : 'realizatsiya'),
    },
    {
        id: 'estetika',
        chapter: 'estetika',
        test: (c) => c.values.prostranstvo < THRESH,
        target: () => 'prostranstvo',
    },
    {
        id: 'terpenie',
        chapter: 'terpenie',
        test: (c) => c.values.sostoyanie >= 7 && (c.values.realizatsiya < THRESH || c.values.produkt < THRESH),
        target: (c) => (c.values.realizatsiya <= c.values.produkt ? 'realizatsiya' : 'produkt'),
    },
    {
        id: 'kontekst',
        chapter: 'pletenie',
        test: (c) => c.low.length >= 1 && c.outLow > c.inLow,
        target: (c) => c.low.find((id) => SPHERES.find((s) => s.id === id)?.grp === 'out') ?? c.weakest,
    },
    {
        id: 'zhivaya',
        chapter: 'evolucia',
        test: (c) => c.low.length === 0,
        target: (c) => c.top,
    },
];
const pct = (r) => Math.round(r * 100);
function matrixVerdict(d, pick, g, t) {
    const v = (pair) => pickOf(pair, pick);
    const view = { skills: d.skills, done: d.done, pct: pct(d.rate), idle: d.idle, spheresTouched: d.spheresTouched, gender: g };
    if (d.skills === 0)
        return { harsh: true, chapter: 'vychislit', text: t.verdict.empty };
    if (d.done === 0)
        return { harsh: true, chapter: 'kapli', text: t.verdict.zero(view, v) };
    const r = d.rate;
    if (r < 0.15)
        return { harsh: false, chapter: 'kapli', text: t.verdict.start(view, v) };
    if (r < 0.25)
        return { harsh: true, chapter: 'kapli', text: t.verdict.harsh(view, v) };
    if (r < 0.45)
        return { harsh: false, text: t.verdict.pace(view, v) };
    if (r < 0.65)
        return { harsh: false, text: t.verdict.big(view, v) };
    if (r < 0.85)
        return { harsh: false, text: t.verdict.worthy(view, v, d.spheresTouched <= 2) };
    return { harsh: false, text: t.verdict.spider(view, v) };
}
/** явное несоответствие колеса и матрицы — повод задать вопрос */
function mismatch(values, d, nm, t) {
    for (const id of SPHERE_IDS) {
        const rate = d.bySphere[id];
        if (rate !== undefined && rate >= 0.6 && values[id] < THRESH) {
            return t.mismatch.doneButLow({ sphere: nm(id), value: values[id] });
        }
    }
    const empty = lowSpheres(values).find((id) => d.bySphere[id] === undefined);
    if (empty && d.skills > 0) {
        return t.mismatch.emptyLow({ sphere: nm(empty) });
    }
    return null;
}
/**
 * второй месяц: цели по навыкам. отстающие перечисляются, больше пяти —
 * предложение скорректировать цели, недостижимые — отдельно
 */
function goalsLine(skills, history, today, t) {
    if (!isSecondMonth({ skills, history }, today))
        return null;
    const withGoal = skills.filter((k) => k.goal);
    if (withGoal.length === 0)
        return t.goals.none;
    const st = withGoal.map((k) => ({ k, s: goalStatus(k, today) }));
    const lost = st.filter((x) => x.s.impossible);
    const behind = st.filter((x) => x.s.behind && !x.s.impossible);
    const parts = [];
    if (lost.length) {
        const items = lost.slice(0, 3).map((x) => ({ name: x.k.name, done: x.s.done, goal: x.s.goal }));
        parts.push(t.goals.lost({ items, more: Math.max(0, lost.length - 3) }));
    }
    if (behind.length > 5)
        parts.push(t.goals.behindMany({ n: behind.length }));
    else if (behind.length)
        parts.push(t.goals.behind({ names: behind.map((x) => x.k.name) }));
    if (!parts.length)
        parts.push(t.goals.ok);
    return parts.join(' ');
}
/** сферы, для которых в озере есть записанные практики */
export const LAKE_SPHERES = ['sostoyanie', 'dengi', 'produkt'];
export function direct(input) {
    const locale = input.locale ?? DEFAULT_LOCALE;
    const t = domainCopy(locale);
    const day = matrixDay(input.skills, input.today);
    const h = seedHash(input.seed);
    const verdict = matrixVerdict(day, h >>> 3, input.gender ?? null, t);
    const nm = (id) => sphereLabel(id, input.sphereNames, locale);
    const c = wheelCtx(input.values, nm);
    let cand = MECHANICS.filter((m) => m.test(c));
    if (cand.length > 1)
        cand = cand.filter((m) => m.id !== input.lastMechanic);
    const mech = cand[h % cand.length];
    const target = mech.target(c);
    const persona = PERSONA[target];
    const n = h >>> 8;
    const prov = pickOf(t.prov[c.weakest], n);
    const wheel = t.mechanics[mech.id](c, (pair) => pickOf(pair, n), prov);
    const odd = mismatch(input.values, day, nm, t);
    const goals = goalsLine(input.skills, input.history, input.today, t);
    // практика в озере — каждый раз. сфера без единиц сегодня важнее сферы механики
    const lakeSet = input.lakeSpheres ?? LAKE_SPHERES;
    const zeroLake = day.zeroSpheres.find((id) => lakeSet.includes(id));
    const lake = zeroLake ?? (lakeSet.includes(target) ? target : lakeSet.find((id) => SPHERES.find((s) => s.id === id)?.grp === SPHERES.find((s) => s.id === target)?.grp) ?? lakeSet[0]);
    const lakeLine = zeroLake
        ? t.lake.zero({ sphere: nm(zeroLake) })
        : lake
            ? t.lake.practice({ sphere: nm(lake) })
            : null;
    // слабая матрица звучит первой: без единиц разговор о колесе — это разговор о погоде
    const first = verdict.harsh ? verdict.text : wheel;
    const second = verdict.harsh ? wheel : verdict.text;
    const body = [first.body, second.lead + ' ' + second.body, goals, odd, t.personas[persona].note, lakeLine]
        .filter(Boolean)
        .join('\n\n');
    const place = PERSONA_PLACE[persona];
    const chapters = [];
    for (const key of [verdict.harsh ? verdict.chapter : undefined, mech.chapter, verdict.chapter]) {
        const ref = key ? chapterRef(key, t) : undefined;
        if (ref && !chapters.some((x) => x.chapterId === ref.chapterId && !x.anchor))
            chapters.push(ref);
    }
    // фраза для старых клиентов, которые ищут место по тексту; новые идут по id
    const anchor = BOOK_ANCHORS[place.anchorId][locale];
    return {
        lead: first.lead,
        body,
        act: first.act,
        refs: {
            mech: mech.id,
            chapters: chapters.slice(0, 3),
            ...(lake ? { lake } : {}),
            persona: {
                name: t.personas[persona].name.toLowerCase(),
                chapterId: CHAPTER_IDS[place.chapter],
                ...(anchor ? { anchor } : {}),
                anchorId: place.anchorId,
            },
        },
        ...(locale !== DEFAULT_LOCALE ? { locale } : {}),
    };
}
//# sourceMappingURL=director.js.map